import { useTranslation } from "react-i18next";
import { Box, Button, Grid, Stack, TextField, Typography } from "@mui/material";
import { ToggleSurface } from "../../../common/core/Surface";
import { Defaults, makeId, Task, useEntityField } from "@emberly/zenith-client";
import { ContentCopy as CopyIcon, Replay as ReplayIcon, OpenInNew as OpenNewIcon } from "@mui/icons-material";
import { useMemo, useState } from "react";
import config from "../../../../config";
import ConfirmDialog from "../../../common/inputs/ConfirmDialog";
import { copyToClipboard } from "../../../../common/helpers";
import { useNotification } from "../../../../providers/NotificationProvider";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()(
  (theme) => ({
    subheader: {
      marginTop: theme.spacing(2)
    },
  })
);

export default function MissionFormLinkCard() {
  const { t } = useTranslation();

  const { classes } = useStyles();
  const { snackbar } = useNotification();
  const [regenAction, setRegenAction] = useState(null);
  const linkId = useEntityField("linkId", Defaults.String);

  const { link, iframe } = useMemo(() => {
    let url = config.isProd ? `https://forms.getassist.app/form/${linkId.value}` : `https://testforms.getassist.app/form/${linkId.value}`;

    return {
      link: url,
      iframe: `<iframe src="${url}" width="600" height="800" style="border:0;"></iframe>`
    };
  }, [linkId.value]);

  const regenLink = async () => {
    try {
      const t = new Task();
      setRegenAction(t);
      await t.wait();
      linkId.onChange(null, makeId());
    } catch (err) {
      console.log(err);
    } finally {
      setRegenAction(null);
    }
  };

  const shareLink = (url) => {
    if (navigator.share) {
      try {
        navigator.share({
          title: t("missionForm:shareableLink"),
          url
        });
      } catch (err) {
        copyToClipboard(url);
        snackbar(t("common:copiedToClipboard"));
      }

    } else {
      copyToClipboard(url);
      snackbar(t("common:copiedToClipboard"));
    }
  };

  const openInNewTab = (link) => {
    window.open(link, "_blank").focus()
  };

  const shareIframe = (text) => {
    copyToClipboard(text);
    snackbar(t("common:copiedToClipboard"));
  };

  return (
    <ToggleSurface title={t("missionForm:links")} md={12} lg={12}>
      <Grid item container spacing={1}>

        <Grid item xs={12}>
          <Typography variant="subtitle1">{t("missionForm:shareableLink")}</Typography>
          <Typography variant="body2" color="textSecondary">{t("missionForm:shareableLinkDescription")}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Stack spacing={1.5}>
            <TextField
              label={t("missionForm:shareableLink")}
              variant="filled"
              fullWidth
              size="small"
              value={link}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="primary" startIcon={<OpenNewIcon />} size="large" onClick={() => openInNewTab(link)}>
                {t("missionForm:openForm")}
              </Button>
              {
                navigator.clipboard ? (
                  <Box>
                    <Button variant="contained" color="neutral" startIcon={<CopyIcon />} size="large" onClick={() => shareLink(link)}>
                      {t("missionForm:copyShareableLink")}
                    </Button>
                  </Box>
                ) : null
              }
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" className={classes.subheader}>{t("missionForm:shareableIframe")}</Typography>
          <Typography variant="body2" color="textSecondary">{t("missionForm:shareableIframeDescription")}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Stack spacing={1.5}>
            <TextField
              label={t("missionForm:shareableIframe")}
              variant="filled"
              fullWidth
              multiline
              size="small"
              value={iframe}
            />
            {
              navigator.clipboard ? (
                <Box>
                  <Button variant="contained" color="neutral" startIcon={<CopyIcon />} size="large" onClick={() => shareIframe(iframe)}>
                    {t("missionForm:copyShareableIframe")}
                  </Button>
                </Box>
              ) : null
            }
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" className={classes.subheader}>{t("missionForm:regenLink")}</Typography>
          <Typography variant="body2" color="textSecondary">{t("missionForm:regenLinkDescription")}</Typography>
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" color="neutral" startIcon={<ReplayIcon />} size="large" onClick={regenLink}>
            {t("missionForm:regenLinkAction")}
          </Button>
        </Grid>
      </Grid>

      <ConfirmDialog
        task={regenAction}
        title={t("missionForm:regenDialog:title")}
        description={t("missionForm:regenDialog:description")}
        confirmText={t("missionForm:regenDialog:action")}
      />

    </ToggleSurface >
  );
}
